import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { defer, merge, Observable, of, Subscription, timer } from 'rxjs';
import { defaultIfEmpty, filter, switchMap, take, timeout } from 'rxjs/operators';
import { canonicalSlug, teamProfilePath, userProfilePath } from '../shared/services/profile-routing/profile-links';
import { MyProfileNavigationService } from '../shared/services/profile-routing/my-profile-navigation.service';

/** CAP's menu-to-route conversion retains component/data, but drops Angular redirectTo. */
@Component({ selector: 'app-central-legacy-redirect', templateUrl: './central-legacy-redirect.component.html',
    styleUrls: ['./central-legacy-redirect.component.scss'] })
export class CentralLegacyRedirectComponent implements OnInit, OnDestroy {
    error = '';
    private subscription: Subscription;
    constructor(private route: ActivatedRoute, private router: Router, private profileNavigation: MyProfileNavigationService) {}
    get profilePage(): boolean {
        return ['own-profile', 'streaker', 'team-slug', 'team-id'].includes(this.route.snapshot.data.centralRedirect);
    }
    get skeletonPage(): 'teams' | 'dashboard' {
        return this.route.snapshot.data.centralRedirect === 'teams' ? 'teams' : 'dashboard';
    }
    ngOnInit(): void { this.open(); }
    open(): void {
        this.subscription?.unsubscribe();
        this.error = '';
        // CAP may create this component after NavigationEnd has already fired.
        // Finish activation on the next task even when there is no future router
        // event. Never wait for the participant to click another link.
        this.subscription = merge(this.router.events.pipe(filter(event => event instanceof NavigationEnd)), timer(0)).pipe(
            take(1), switchMap(() => this.destination()), take(1), defaultIfEmpty(false), timeout(30000)
        ).subscribe(opened => {
            if (!opened && !this.error) { this.error = 'We couldn’t open this page. Try again or return to Streaker Central.'; }
        }, () => this.error = 'We couldn’t open this page. Try again or return to Streaker Central.');
    }
    private destination(): Observable<boolean> {
        return defer(() => {
            try {
                const snapshot = this.route.snapshot, params = snapshot.paramMap;
                if (snapshot.data.centralRedirect === 'own-profile') { return this.profileNavigation.open(true); }
                let target: string;
                switch (snapshot.data.centralRedirect) {
                    case 'registration': target = '/challenge-registration'; break;
                    case 'streaker': target = userProfilePath(params.get('slug')); break;
                    case 'teams': target = '/streaker-central/teams'; break;
                    case 'team-slug': {
                        const id = Number(params.get('challengeId'));
                        teamProfilePath(id, params.get('slug')); // Validate historical challenge identity.
                        target = '/streaker-central/teams/' + id + '/' + canonicalSlug(params.get('slug'));
                        break;
                    }
                    case 'team-id': {
                        const id = Number(params.get('teamId'));
                        if (!Number.isSafeInteger(id) || id <= 0) { throw new Error('Invalid team link'); }
                        target = '/streaker-central/teams/id/' + id;
                        break;
                    }
                    default: throw new Error('Unknown Central link');
                }
                const url = this.router.createUrlTree([target], { queryParams: snapshot.queryParams, fragment: snapshot.fragment });
                return this.router.navigateByUrl(url, { replaceUrl: true });
            } catch {
                this.error = 'This link is unavailable. Open Teams or Streakers from Streaker Central.';
                return of(false);
            }
        });
    }
    ngOnDestroy(): void { this.subscription?.unsubscribe(); }
}
